
import { supabase } from '@/integrations/supabase/client';

/**
 * Uploads an image to the specified Supabase bucket
 * @param file The file to upload
 * @param bucketName The name of the bucket to upload to
 * @returns The public URL of the uploaded image
 */
export const uploadImage = async (file: File, bucketName: string): Promise<string> => {
  if (!file) {
    throw new Error('No file provided');
  }

  // Generate a unique file name
  const fileExt = file.name.split('.').pop();
  const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
  
  // Upload the file to Supabase Storage
  const { data, error } = await supabase.storage
    .from(bucketName)
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (error) {
    throw error;
  }

  // Get the public URL
  const { data: publicUrlData } = supabase.storage
    .from(bucketName)
    .getPublicUrl(fileName);
  
  return publicUrlData.publicUrl;
};

/**
 * Removes an image from Supabase storage
 * @param url The URL of the image to remove
 * @param bucketName The name of the bucket the image is in
 */
export const removeImage = async (url: string, bucketName: string): Promise<void> => {
  if (!url) return;
  
  try {
    // Extract the file path from the URL
    const filePath = url.split(`${bucketName}/`)[1];
    if (!filePath) return;
    
    // Delete the file from Supabase Storage
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);
    
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error removing image:', error);
    throw error;
  }
};
